/**
 * Vincent B967 (Eric 2026-02-14 SPEC STRICK PROD) — Ecran admin qui liste
 * les termes du dictionnaire marques `status=rejected` (via B961 cleanup)
 * et propose de les restaurer en cas de faux positif.
 *
 * Route : /dashboard/admin/dict-rejected — accessible aux concepteurs+.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowClockwise, SpinnerGap, Trash } from "@phosphor-icons/react";
import { useLang } from "../../contexts/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL;

export default function DictRejectedTermsPage() {
  const { lang } = useLang();
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [restoring, setRestoring] = useState(false);
  const [restoringId, setRestoringId] = useState(null);
  const [query, setQuery] = useState("");
  // Vincent B975 (Eric 2026-02-26 SPEC STRICK PROD) — Suppression
  // definitive : 2-step confirm (bouton devient rouge « Confirmer »).
  const [purging, setPurging] = useState(false);
  const [needConfirmPurge, setNeedConfirmPurge] = useState(false);
  // Vincent B986 (Eric 2026-02-26 SPEC STRICK PROD) — Audit trail des
  // purges définitives, chargé à l'ouverture de la page + après chaque
  // purge. Collapsible section en bas de page.
  const [purgeAudit, setPurgeAudit] = useState([]);
  const [purgeAuditLoading, setPurgeAuditLoading] = useState(false);

  const loadPurgeAudit = async () => {
    setPurgeAuditLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/api/jargon/cleanup/purge/audit?limit=50`,
        { withCredentials: true },
      );
      setPurgeAudit(data?.audit || []);
    } catch (_e) { /* silencieux */ }
    finally { setPurgeAuditLoading(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      // Utilise include_rejected=true (B961) + filtre côté client sur
      // status=rejected pour ne montrer QUE les rejetés.
      const { data } = await axios.get(
        `${API}/api/jargon?include_rejected=true`,
        { withCredentials: true },
      );
      const list = (Array.isArray(data) ? data : []).filter((e) => e.status === "rejected");
      setEntries(list);
      setSelected(new Set());
    } catch (e) {
      toast.error((lang === "fr" ? "Erreur : " : "Error: ") + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); loadPurgeAudit(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const doRestore = async () => {
    if (selected.size === 0) return;
    setRestoring(true);
    try {
      const { data } = await axios.post(
        `${API}/api/jargon/cleanup/restore`,
        { ids: [...selected] },
        { withCredentials: true },
      );
      toast.success(
        lang === "fr"
          ? `${data.restored_count} terme(s) restauré(s) et rendus visibles dans le dictionnaire.`
          : `${data.restored_count} term(s) restored and visible again in the dictionary.`,
      );
      await load();
    } catch (e) {
      toast.error((lang === "fr" ? "Erreur : " : "Error: ") + (e?.response?.data?.detail || e.message));
    } finally {
      setRestoring(false);
    }
  };

  // Bogue 967 REFAIT (Vincent 2026-06-25) — « le bouton Restaurer n'apparaît
  // pas ». Il n'existait que dans la barre de sélection : il fallait d'abord
  // cocher une ligne pour le voir apparaître. Chaque terme rejeté porte donc
  // maintenant son propre bouton « Restaurer », visible immédiatement.
  const restoreOne = async (id) => {
    setRestoringId(id);
    try {
      await axios.post(
        `${API}/api/jargon/cleanup/restore`,
        { ids: [id] },
        { withCredentials: true },
      );
      toast.success(lang === "fr" ? "Terme restauré dans le dictionnaire." : "Term restored in the dictionary.");
      await load();
    } catch (e) {
      toast.error((lang === "fr" ? "Erreur : " : "Error: ") + (e?.response?.data?.detail || e.message));
    } finally {
      setRestoringId(null);
    }
  };

  // Vincent B975 (Eric 2026-02-26 SPEC STRICK PROD) — Suppression
  // definitive. Le bouton est en 2 clics (arm rouge puis exécute).
  // L'utilisateur peut annuler en cliquant « Effacer sélection » ou
  // en cliquant le bouton « Annuler » qui apparaît à côté.
  const doPurge = async () => {
    if (selected.size === 0) return;
    if (!needConfirmPurge) { setNeedConfirmPurge(true); return; }
    setNeedConfirmPurge(false);
    setPurging(true);
    try {
      const { data } = await axios.post(
        `${API}/api/jargon/cleanup/purge`,
        { ids: [...selected] },
        { withCredentials: true },
      );
      const n = data.purged_count || 0;
      const skipped = data.skipped_not_rejected || 0;
      if (skipped > 0) {
        toast.warning(
          lang === "fr"
            ? `${n} terme(s) supprimé(s) définitivement. ${skipped} ignoré(s) (pas en statut « rejeté »).`
            : `${n} term(s) permanently deleted. ${skipped} ignored (not in 'rejected' status).`,
        );
      } else {
        toast.success(
          lang === "fr"
            ? `${n} terme(s) supprimé(s) définitivement du dictionnaire.`
            : `${n} term(s) permanently deleted from dictionary.`,
        );
      }
      await load();
      // Vincent B986 (Eric 2026-02-26 SPEC STRICK PROD) — Refresh
      // audit log après une purge réussie pour visualiser l'action.
      await loadPurgeAudit();
    } catch (e) {
      toast.error((lang === "fr" ? "Erreur : " : "Error: ") + (e?.response?.data?.detail || e.message));
    } finally {
      setPurging(false);
    }
  };

  const q = query.trim().toLowerCase();
  const filtered = q
    ? entries.filter((e) => (e.term || "").toLowerCase().includes(q) || (e.definition || "").toLowerCase().includes(q))
    : entries;

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="dict-rejected-page">
      {/* Bogue 1220 (Eric 2026-06-28) — CAUSE : on rejette à trois endroits
          (terme refusé à la validation, terme écarté par le nettoyage, doublon
          « conservé ») et cette page n'en lisait qu'UN. Le doublon conservé ne
          vivait même que dans le navigateur. D'où une page vide quoi qu'on
          rejette. Le journal ci-dessous lit les trois sources. */}
      <RejectionsJournal />
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-semibold text-zinc-900">
            {lang === "fr" ? "Termes rejetés du dictionnaire" : "Rejected dictionary terms"}
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            {lang === "fr"
              ? "Termes marqués comme non pertinents par le nettoyage. Sélectionne-les pour les restaurer si tu détectes un faux positif. Le journal ci-dessus liste TOUS les rejets, toutes provenances confondues."
              : "Terms flagged as irrelevant by the B960.d cleanup. Select them to restore if you spot a false positive."}
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="text-xs text-[#002FA7] hover:underline disabled:opacity-50"
          data-testid="dict-rejected-refresh-btn"
        >
          {lang === "fr" ? "Rafraîchir" : "Refresh"}
        </button>
      </div>

      <div className="flex items-center gap-3 mb-3">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={lang === "fr" ? "Rechercher…" : "Search…"}
          className="flex-1 px-3 py-2 border border-zinc-300 rounded-md text-sm"
          data-testid="dict-rejected-search-input"
        />
        <span className="text-xs text-zinc-500 whitespace-nowrap">
          {filtered.length}{" "}
          {lang === "fr" ? "terme(s)" : "term(s)"}
        </span>
      </div>

      {selected.size > 0 && (
        <div
          className="mb-3 flex items-center justify-between px-3 py-2 bg-teal-50 border border-teal-200 rounded-md"
          data-testid="dict-rejected-selection-bar"
        >
          <span className="text-xs text-teal-900">
            {selected.size}{" "}
            {lang === "fr" ? "sélectionné(s)" : "selected"}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { setSelected(new Set()); setNeedConfirmPurge(false); }}
              className="text-xs px-2 py-1 rounded bg-white border border-zinc-300 hover:bg-zinc-50"
              data-testid="dict-rejected-clear-selection"
            >
              {lang === "fr" ? "Effacer" : "Clear"}
            </button>
            <button
              onClick={doRestore}
              disabled={restoring || purging}
              className="text-xs font-semibold px-3 py-1 rounded bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50 inline-flex items-center gap-1.5"
              data-testid="dict-rejected-restore-btn"
            >
              {restoring
                ? <SpinnerGap size={12} className="animate-spin" />
                : <ArrowClockwise size={12} />}
              {lang === "fr" ? `Restaurer ${selected.size} terme(s)` : `Restore ${selected.size} term(s)`}
            </button>
            {/* Vincent B975 (Eric 2026-02-26 SPEC STRICK PROD) —
                Suppression definitive avec 2-step confirm rouge. */}
            {needConfirmPurge && !purging && (
              <button
                onClick={() => setNeedConfirmPurge(false)}
                className="text-xs px-2 py-1 rounded bg-white border border-zinc-300 hover:bg-zinc-50"
                data-testid="dict-rejected-purge-cancel-btn"
              >
                {lang === "fr" ? "Annuler" : "Cancel"}
              </button>
            )}
            <button
              onClick={doPurge}
              disabled={restoring || purging}
              className={`text-xs font-semibold px-3 py-1 rounded text-white disabled:opacity-50 inline-flex items-center gap-1.5 ${
                needConfirmPurge ? "bg-red-800 hover:bg-red-900 ring-2 ring-red-300" : "bg-red-600 hover:bg-red-700"
              }`}
              data-testid="dict-rejected-purge-btn"
              title={lang === "fr"
                ? "Supprime définitivement les termes sélectionnés. Action IRRÉVERSIBLE."
                : "Permanently delete the selected terms. IRREVERSIBLE."}
            >
              {purging
                ? <SpinnerGap size={12} className="animate-spin" />
                : <Trash size={12} weight="bold" />}
              {purging
                ? (lang === "fr" ? "Suppression…" : "Deleting…")
                : needConfirmPurge
                  ? (lang === "fr" ? `⚠️ Confirmer (${selected.size})` : `⚠️ Confirm (${selected.size})`)
                  : (lang === "fr" ? `Supprimer définitivement (${selected.size})` : `Delete permanently (${selected.size})`)}
            </button>
          </div>
        </div>
      )}
      {needConfirmPurge && selected.size > 0 && !purging && (
        <p
          className="mb-3 -mt-1 px-3 py-1.5 text-[11px] text-red-800 bg-red-50 border border-red-200 rounded"
          data-testid="dict-rejected-purge-confirm-hint"
        >
          {lang === "fr"
            ? `⚠️ Action IRRÉVERSIBLE : les ${selected.size} terme(s) seront supprimés définitivement du dictionnaire. Cliquez « Confirmer » pour poursuivre, ou « Annuler » pour revenir en arrière.`
            : `⚠️ IRREVERSIBLE: the ${selected.size} term(s) will be permanently deleted from the dictionary. Click "Confirm" to proceed, or "Cancel" to go back.`}
        </p>
      )}

      {loading ? (
        <div className="py-16 text-center text-sm text-zinc-500">
          <SpinnerGap size={20} className="inline animate-spin mr-2" />
          {lang === "fr" ? "Chargement…" : "Loading…"}
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center text-sm text-zinc-500" data-testid="dict-rejected-empty">
          {q
            ? (lang === "fr" ? `Aucun terme rejeté ne correspond à « ${q} »` : `No rejected term matches "${q}"`)
            : (lang === "fr" ? "Aucun terme rejeté. Le dictionnaire est propre." : "No rejected terms. Dictionary is clean.")}
          {/* Bogue 967 refait — Une page vide sans explication laisse croire
              que la fonction est cassée : on dit d'où viennent ces termes et
              où sera le bouton « Restaurer ». */}
          {!q && (
            <p className="mt-2 text-xs text-zinc-400 max-w-lg mx-auto" data-testid="dict-rejected-empty-hint">
              {lang === "fr"
                ? "Les termes arrivent ici uniquement quand le nettoyage du dictionnaire les marque « non pertinents ». Chaque terme listé porte alors son propre bouton « Restaurer »."
                : "Terms land here only when the dictionary cleanup flags them as irrelevant. Each listed term then carries its own “Restore” button."}
            </p>
          )}
        </div>
      ) : (
        <div className="border border-zinc-200 rounded-md overflow-hidden bg-white">
          <ul className="divide-y divide-zinc-100" data-testid="dict-rejected-list">
            {filtered.map((e) => {
              const isSel = selected.has(e.id);
              return (
                <li
                  key={e.id}
                  className={`px-3 py-2 flex items-start gap-2 cursor-pointer ${isSel ? "bg-teal-50" : "hover:bg-zinc-50"}`}
                  onClick={() => toggle(e.id)}
                  data-testid={`dict-rejected-row-${e.id}`}
                >
                  <input
                    type="checkbox"
                    checked={isSel}
                    onChange={() => toggle(e.id)}
                    onClick={(ev) => ev.stopPropagation()}
                    className="mt-1"
                    data-testid={`dict-rejected-checkbox-${e.id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-zinc-900">{e.term}</div>
                    {e.definition && (
                      <div className="text-xs text-zinc-500 line-clamp-2">{e.definition}</div>
                    )}
                    <div className="text-[10px] text-zinc-400 mt-0.5">
                      {e.rejected_at && (
                        <>
                          {lang === "fr" ? "Rejeté le " : "Rejected on "}
                          {new Date(e.rejected_at).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US")}
                          {" · "}
                        </>
                      )}
                      {e.rejected_reason || "b961_cleanup"}
                    </div>
                  </div>
                  {/* Bogue 967 refait — Bouton « Restaurer » sur CHAQUE ligne :
                      il n'apparaissait qu'après avoir coché une case. */}
                  <button
                    onClick={(ev) => { ev.stopPropagation(); restoreOne(e.id); }}
                    disabled={restoringId === e.id || restoring || purging}
                    className="flex-shrink-0 text-xs font-semibold px-3 py-1.5 rounded bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50 inline-flex items-center gap-1.5"
                    data-testid={`dict-rejected-restore-row-btn-${e.id}`}
                    title={lang === "fr"
                      ? "Remettre ce terme dans le dictionnaire (faux positif du nettoyage)"
                      : "Put this term back in the dictionary (cleanup false positive)"}
                  >
                    {restoringId === e.id
                      ? <SpinnerGap size={12} className="animate-spin" />
                      : <ArrowClockwise size={12} />}
                    {lang === "fr" ? "Restaurer" : "Restore"}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Vincent B986 (Eric 2026-02-26 SPEC STRICK PROD) — Historique
          des purges définitives (audit trail Loi 25). Collapsible ;
          liste les 50 dernières purges avec qui/quand/combien +
          snapshot des termes purgés. */}
      <details
        className="mt-6 border border-zinc-200 rounded-lg bg-zinc-50"
        data-testid="dict-rejected-purge-audit"
      >
        <summary className="cursor-pointer px-4 py-2 text-sm font-semibold text-zinc-800 hover:bg-zinc-100 select-none">
          {lang === "fr" ? "🗂 Historique des purges définitives" : "🗂 Permanent purge history"}{" "}
          <span className="font-mono text-xs text-zinc-500">({purgeAudit.length})</span>
        </summary>
        <div className="p-4 pt-2">
          {purgeAuditLoading ? (
            <div className="text-xs text-zinc-500">{lang === "fr" ? "Chargement…" : "Loading…"}</div>
          ) : purgeAudit.length === 0 ? (
            <div className="text-xs text-zinc-500 italic">
              {lang === "fr"
                ? "Aucune purge définitive enregistrée pour cette organisation."
                : "No permanent purges recorded for this organization."}
            </div>
          ) : (
            <ul className="space-y-2 text-xs" data-testid="dict-rejected-purge-audit-list">
              {purgeAudit.map((row) => (
                <li
                  key={row.id}
                  className="border border-zinc-200 rounded p-2 bg-white"
                  data-testid={`dict-rejected-purge-audit-row-${row.id}`}
                >
                  <div className="flex items-center justify-between text-zinc-700 mb-1">
                    <span>
                      <span className="font-semibold">{row.purged_by_email || "?"}</span>
                      <span className="text-zinc-400"> · {row.purged_by_role || "?"}</span>
                    </span>
                    <span className="text-zinc-500 font-mono">
                      {row.purged_at ? new Date(row.purged_at).toLocaleString(lang === "fr" ? "fr-FR" : "en-US") : "?"}
                    </span>
                  </div>
                  <div className="text-zinc-800">
                    <span className="font-mono font-bold text-red-700">{row.purged_count}</span>{" "}
                    {lang === "fr" ? "terme(s) supprimé(s) définitivement :" : "term(s) permanently deleted:"}
                  </div>
                  {(row.terms_snapshot || []).length > 0 && (
                    <div className="mt-1 text-zinc-600 flex flex-wrap gap-1">
                      {(row.terms_snapshot || []).slice(0, 10).map((t, i) => (
                        <span key={i} className="inline-block px-1.5 py-0.5 bg-red-50 border border-red-200 rounded text-[10px] font-mono">
                          {t.term || t.id}
                        </span>
                      ))}
                      {(row.terms_snapshot || []).length > 10 && (
                        <span className="text-[10px] text-zinc-500 italic">
                          +{(row.terms_snapshot || []).length - 10} {lang === "fr" ? "autres" : "more"}
                        </span>
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </details>
    </div>
  );
}

/**
 * Bogue 1220 — Journal unifié des rejets : refus à la validation, termes
 * écartés par le nettoyage, doublons conservés. Daté, avec auteur et motif.
 */
const KIND_LABELS = {
  terme_refuse_validation: "Refusé à la validation",
  terme_ecarte_nettoyage: "Écarté par le nettoyage",
  doublon_conserve: "Doublon conservé (les deux gardés)",
  // Tâche 1269 (Eric 2026-06-29) — Tag refusé sur une fiche vidéo.
  tag_video_refuse: "Tag refusé sur une fiche",
};

const RejectionsJournal = () => {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/api/jargon/rejections/journal`, { withCredentials: true })
      .then(({ data }) => { if (alive) setRows(data?.rejections || []); })
      .catch(() => { if (alive) setRows([]); })
      .finally(() => { if (alive) setBusy(false); });
    return () => { alive = false; };
  }, []);

  if (busy) return null;

  return (
    <div
      className="mb-5 rounded-md border border-zinc-200 bg-white px-3 py-2"
      data-testid="dict-rejections-journal"
    >
      <p className="text-sm font-semibold text-zinc-900">
        Journal des rejets
        <span
          className="ml-2 px-1.5 py-0.5 rounded-full bg-zinc-100 text-zinc-700 text-[10px] font-bold tabular-nums"
          data-testid="dict-rejections-journal-count"
        >
          {rows.length}
        </span>
      </p>
      {rows.length === 0 ? (
        <p className="text-xs text-zinc-500 mt-1" data-testid="dict-rejections-journal-empty">
          Aucun rejet enregistré, toutes provenances confondues : ni refus à la
          validation, ni terme écarté par le nettoyage, ni doublon conservé.
        </p>
      ) : (
        <ul className="mt-2 flex flex-col gap-1" data-testid="dict-rejections-journal-list">
          {rows.map((r, i) => (
            <li
              key={`${r.kind}-${r.label}-${i}`}
              className="flex items-center gap-2 flex-wrap text-xs border border-zinc-100 rounded px-2 py-1.5"
              data-testid={`dict-rejection-row-${i}`}
            >
              <span className="px-1.5 py-0.5 rounded-full bg-zinc-100 text-zinc-700 text-[10px] font-semibold">
                {KIND_LABELS[r.kind] || r.kind}
              </span>
              <span className="font-semibold text-zinc-900">{r.label}</span>
              <span className="text-[11px] text-zinc-500">
                {r.at ? String(r.at).slice(0, 16).replace("T", " ") : "date inconnue"}
                {r.by ? ` · ${r.by}` : ""}
                {r.reason ? ` · ${r.reason}` : ""}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
